/**
 * Environment-derived configuration for the admin-v2 app.
 *
 * Following the monorepo convention, the API host is provided as a bare domain
 * via `VITE_APP_API_DOMAIN` (for example `api.rumbledirect.com`) and is always
 * addressed over HTTPS. Resolution lives here, free of import-time side effects,
 * so it stays trivially testable and fails only on the code path that needs it.
 */

/**
 * Builds the API base URL from a bare API domain.
 *
 * @param domain The API host without a protocol, e.g. `api.rumbledirect.com`.
 *   A trailing slash is tolerated and removed.
 * @returns The HTTPS base URL, e.g. `https://api.rumbledirect.com`.
 * @throws {Error} When the domain is missing or blank; the app cannot reach the
 *   API without it.
 */
export function resolveApiBaseUrl(domain: string | undefined): string {
  const normalized = domain?.trim().replace(/\/+$/, "") ?? "";

  if (normalized === "") {
    throw new Error(
      "Missing API domain. Set VITE_APP_API_DOMAIN to the API host (for example api.rumbledirect.com).",
    );
  }

  return `https://${normalized}`;
}

/**
 * Resolves the API base URL from the current Vite environment.
 *
 * Read lazily rather than at module load so a missing variable only breaks the
 * code path that actually calls the API, not the entire module graph.
 */
export function getApiBaseUrl(): string {
  return resolveApiBaseUrl(import.meta.env.VITE_APP_API_DOMAIN);
}

/**
 * Builds the base URL of the driver-facing "my" app from a bare domain.
 *
 * @param domain The my host without a protocol, e.g. `my.rumbledirect.com`.
 *   A trailing slash is tolerated and removed.
 * @returns The HTTPS base URL, e.g. `https://my.rumbledirect.com`.
 * @throws {Error} When the domain is missing or blank.
 */
export function resolveMyBaseUrl(domain: string | undefined): string {
  const normalized = domain?.trim().replace(/\/+$/, "") ?? "";

  if (normalized === "") {
    throw new Error(
      "Missing my domain. Set VITE_APP_MY_DOMAIN to the my host (for example my.rumbledirect.com).",
    );
  }

  return `https://${normalized}`;
}

/**
 * Resolves the my app's base URL from the current Vite environment.
 *
 * Read lazily rather than at module load so a missing variable only breaks the
 * links that point at the my app, not the entire module graph.
 */
export function getMyBaseUrl(): string {
  return resolveMyBaseUrl(import.meta.env.VITE_APP_MY_DOMAIN);
}

/**
 * The Keycloak realm every environment authenticates against. It is a constant
 * (not an environment variable) because it never differs between deployments.
 */
const KEYCLOAK_REALM = "RumbleDirect";

/**
 * OIDC scopes requested at login. `organization:*` yields the Keycloak
 * organization claim that drives tenant (affiliate) resolution.
 */
const OIDC_SCOPE = "organization:* openid profile email";

/** The environment-derived OIDC settings shared by the auth layer. */
export interface AuthConfig {
  /** OIDC authority: the Keycloak realm issuer URL. */
  readonly authority: string;
  /** Public OIDC client id. */
  readonly clientId: string;
  /** Requested OIDC scopes, including the organization claim used for tenancy. */
  readonly scope: string;
}

/**
 * Builds the OIDC settings from a bare auth domain and client id.
 *
 * @param authDomain The Keycloak host without a protocol, e.g. `auth.rumbledirect.com`.
 *   A trailing slash is tolerated and removed.
 * @param clientId The public OIDC client id, e.g. `api-platform-webapp`.
 * @throws {Error} When the domain or client id is missing or blank; the app
 *   cannot authenticate without them.
 */
export function resolveAuthConfig(
  authDomain: string | undefined,
  clientId: string | undefined,
): AuthConfig {
  const normalizedDomain = authDomain?.trim().replace(/\/+$/, "") ?? "";
  if (normalizedDomain === "") {
    throw new Error(
      "Missing auth domain. Set VITE_APP_AUTH_DOMAIN to the Keycloak host (for example auth.rumbledirect.com).",
    );
  }

  const normalizedClientId = clientId?.trim() ?? "";
  if (normalizedClientId === "") {
    throw new Error(
      "Missing auth client id. Set VITE_APP_AUTH_CLIENT_ID to the Keycloak client (for example api-platform-webapp).",
    );
  }

  return {
    authority: `https://${normalizedDomain}/realms/${KEYCLOAK_REALM}`,
    clientId: normalizedClientId,
    scope: OIDC_SCOPE,
  };
}

/**
 * Resolves the OIDC settings from the current Vite environment.
 *
 * Read lazily rather than at module load so a missing variable only breaks the
 * authentication path, not the entire module graph.
 */
export function getAuthConfig(): AuthConfig {
  return resolveAuthConfig(
    import.meta.env.VITE_APP_AUTH_DOMAIN,
    import.meta.env.VITE_APP_AUTH_CLIENT_ID,
  );
}
